import { Chip, type ChipTone } from './Chip';

/**
 * A line's warehouse-release verdict, exactly as the server computes it
 * (services/dealView `buildDealView` → domain `evaluateRelease`). Carried per line on the deal payload.
 */
export interface LineRelease {
  verdict: 'ok' | 'override' | 'blocked';
  blocking: { code: string; message: string }[];
  /** non-blocking advisories the gate raised (e.g. `{code:'confidence'}` when cost-confidence < confFloor). */
  warnings: { code: string; message: string }[];
}

/** ONE mapping verdict → icon/label/tone, so every surface reads ship-readiness identically. */
const VERDICT_META: Record<
  LineRelease['verdict'],
  { icon: string; label: string; tone: ChipTone }
> = {
  ok: { icon: '✅', label: 'آزاد برای ترخیص', tone: 'success' },
  override: { icon: '⚠', label: 'نیازمند تأیید مدیر', tone: 'warn' },
  blocked: { icon: '⛔', label: 'غیرقابل ترخیص', tone: 'danger' },
};

/**
 * The release-gate verdict for one line, read identically wherever ship-readiness is shown (E16-09):
 * the deal item editor at PRICING time and the dispatch panel. It surfaces the SAME verdict the
 * warehouse enforces — it never recomputes the gate. On the deal form the verdict reflects the deal's
 * last-computed (saved) state; the live margin beside it updates as the owner types, and the verdict
 * refreshes when the deal is saved. The price-independent hard blocks (بها ناقص / سربارِ ماه ثبت‌نشده)
 * are always accurate, which is the surprise E16-09 removes: the owner learns a line can't ship while
 * he is still pricing it, not first at dispatch.
 */
export function ReleaseVerdictChip({
  release,
  hideOk = false,
  showReason = false,
}: {
  release: LineRelease | undefined;
  /** when true, an OK verdict renders nothing — keeps dense rows quiet until there is something to act on */
  hideOk?: boolean;
  /** when true, append the blocking reasons inline (they are always in the tooltip regardless) */
  showReason?: boolean;
}) {
  if (!release) return null;
  const { verdict, blocking } = release;
  if (verdict === 'ok' && hideOk) return null;
  const meta = VERDICT_META[verdict];
  const reason = blocking.map((b) => b.message).join(' • ');
  return (
    <span title={reason || undefined}>
      <Chip tone={meta.tone}>
        {meta.icon} {meta.label}
      </Chip>
      {showReason && reason ? (
        <span className="muted" style={{ fontSize: 11 }}>
          {' — '}
          {reason}
        </span>
      ) : null}
    </span>
  );
}
